import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FiDownload, FiInfo } from "react-icons/fi";
import { FaCheck, FaTimes } from "react-icons/fa";
import {
  DownloadExcelBill,
  GenericTable,
  Pagination,
  Tooltip,
  Spinner,
  FilterCard,
} from "../../Components/Common";
import { UserContext } from "../../Context";
import { ROLES } from "../../Constants/Strings";
// import { RESET_PAGINATION } from "../../Constants/Strings";
import { getAllUsers } from "../../Services/userService";
import * as attendanceService from "../../Services/attendanceService";
import * as route from "../../routes";
import {
  MESSAGES,
  ATTENDANCE_COLUMNS,
  ATTENDANCE_LABELS,
  PENDING_L1_APPROVAL,
  PENDING_L2_APPROVAL,
  EXCEL_FILE_COLUMNS,
  STATUS_LABELS,
  ACTION_APPROVED,
  ACTION_REJECTED,
  STATUS_FILTER_OPTIONS,
} from "./constants";
import AttendanceForm from "./Attendance";
import MobileAttendanceCard from "./Component/MobileAttendanceCard";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";

const ListAttendance = () => {
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    count: 0,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [tempFilters, setTempFilters] = useState({});
  const [users, setUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const { user } = useContext(UserContext);

  const navigate = useNavigate();

  const getAttendanceOverview = async (params) => {
    setLoading(true);
    try {
      const { page = 1, limit = 10 } = params;
      const offset = (page - 1) * limit;
      const response = await attendanceService.getAttendanceList({
        limit,
        offset,
        ...(user?.role === ROLES.SITE_ENGINEER ? { user: user.id } : {}),
        ...filters,
      });
      if (response.status === 200) {
        setAttendance(response.data.results);
        setPagination((prev) => ({
          ...prev,
          page,
          limit,
          count: response.data.count,
        }));
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.REQUEST_ERROR;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, action) => {
    try {
      const response = await attendanceService.reviewAttendance(id, { action });
      if (response.status === 200) {
        toast.success(MESSAGES.ATTENDANCE_UPDATE_SUCCESS(action));
        getAttendanceOverview(
          { page: pagination.page, limit: pagination.limit },
          true
        );
      } else {
        const msg =
          extractErrorMessage(response) ||
          MESSAGES.ATTENDANCE_UPDATE_UNAUTHORIZED;
        toast.error(msg);
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || MESSAGES.ATTENDANCE_UPDATE_FAILED;
      toast.error(msg);
    }
  };

  const renderFields = (key, rowData) => {
    const value = rowData[key];

    if (key === "status") {
      const { text, className } =
        STATUS_LABELS[rowData.status] || STATUS_LABELS.DEFAULT;
      return <span className={className}>{text}</span>;
    }

    if (
      ["checkin_time", "checkout_time"].includes(key) &&
      typeof value === "string"
    ) {
      return value.split(".")[0];
    }

    if (rowData.is_backdated === "Yes") {
      if (key === "checkin_photo_url" || key === "checkout_photo_url") {
        return "NA";
      }
    }

    switch (key) {
      // Comment if you want to remove the Custom Class: Discuss One
      case "is_backdated":
        return rowData.is_backdated === "Yes" ? (
          <span className="text-primary font-bold">YES</span>
        ) : (
          <span className="text-gray-600 font-medium">NO</span>
        );
      case "status":
        return (
          <span>
            {rowData.status.charAt(0).toUpperCase() + rowData.status.slice(1)}
          </span>
        );
      case "actions":
        const isL1Pending = rowData.status === PENDING_L1_APPROVAL;
        const isL2Pending = rowData.status === PENDING_L2_APPROVAL;

        return (
          <div className="flex gap-3 items-center">
            {/* View */}
            <Tooltip text="View Attendance">
              <FiInfo
                className="cursor-pointer text-primary hover:text-secondary text-lg"
                onClick={() =>
                  navigate(route.VIEW_DETAILED_PAGE.replace(":id", rowData.id))
                }
              />
            </Tooltip>

            {/* Approve/Reject for Project Engineer (L1) */}
            {user?.role === ROLES.PROJECT_ENGINEER && isL1Pending && (
              <>
                <Tooltip text="Approve">
                  <FaCheck
                    className="cursor-pointer text-green-600"
                    onClick={() =>
                      handleStatusChange(rowData.id, ACTION_APPROVED)
                    }
                  />
                </Tooltip>
                <Tooltip text="Reject">
                  <FaTimes
                    className="cursor-pointer text-red-600"
                    onClick={() =>
                      handleStatusChange(rowData.id, ACTION_REJECTED)
                    }
                  />
                </Tooltip>
              </>
            )}

            {/* Approve/Reject for Project Manager (L2) */}
            {user?.role === ROLES.PROJECT_MANAGER && isL2Pending && (
              <>
                <Tooltip text="Approve">
                  <FaCheck
                    className="cursor-pointer text-green-600"
                    onClick={() =>
                      handleStatusChange(rowData.id, ACTION_APPROVED)
                    }
                  />
                </Tooltip>
                <Tooltip text="Reject">
                  <FaTimes
                    className="cursor-pointer text-red-600"
                    onClick={() =>
                      handleStatusChange(rowData.id, ACTION_REJECTED)
                    }
                  />
                </Tooltip>
              </>
            )}
          </div>
        );
      default:
        return value;
    }
  };

  useEffect(() => {
    getAttendanceOverview({
      page: pagination.page,
      limit: pagination.limit,
      ...filters,
    });
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = (await getAllUsers({ limit: 10, offset: 0 })).data.results;
        setUsers(res);
        // Format for CommonFilterCard
        const formattedOptions = [
          { value: "", label: "All"},
          ...res.map((user) => ({
          value: user.id,
          label: `${user.first_name} ${user.last_name}`,
        })),
      ];
        setUserOptions(formattedOptions);
      } catch (err) {
        toast.error(MESSAGES.USER_FETCH_ERROR);
      }
    };
    fetchUsers();
  }, []);

  const downloadExcelData = () => {
    DownloadExcelBill({
      fetchFn: attendanceService.getAttendanceList,
      fieldMappings: EXCEL_FILE_COLUMNS,
      fileName: "Attendance_Data.xlsx",
      filters: tempFilters,
    });
  };

  return (
    <div className="px-4 my-4">
      {/* Header Section */}
      <div className="flex flex-col">
        <div className="flex items-center justify-between mb-3 mt-4 md:mt-0">
          <div className="text-xl md:text-2xl text-primary font-semibold">
            Attendance Overview
          </div>
          <div className="flex gap-3">
            <Tooltip text="Download Excel">
              <button
                onClick={downloadExcelData}
                className="px-4 py-2 bg-primary text-white rounded-lg shadow hover:bg-secondary transition-colors"
              >
                <FiDownload />
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="mb-4">
          <FilterCard
            tempFilters={tempFilters}
            setTempFilters={setTempFilters}
            onFilterChange={(updated) => {
              setTempFilters(updated); // keep temp filters updated
              setFilters(updated); // apply filters
              setPagination((prev) => ({ ...prev, page: 1 })); // reset to first page
            }}
            fields={[
              {
                key: "status",
                label: "Status",
                type: "select",
                options: STATUS_FILTER_OPTIONS,
              },
              ...(user?.role !== ROLES.SITE_ENGINEER &&
              user?.role !== ROLES.PROJECT_ENGINEER
                ? [
                    {
                      key: "user",
                      label: "User",
                      type: "select",
                      options: userOptions,
                    },
                  ]
                : []),
              { key: "date", label: "Date", type: "date" },
            ]}
          />
        </div>
      </div>

      {/* Section for history */}
      {loading ? (
        <div className="flex justify-center items-center h-[65vh]">
          <Spinner />
        </div>
      ) : isOpen ? (
        // Update the list after adding the attendance from child
        <AttendanceForm onClose={() => setIsOpen(false)} />
      ) : attendance ? (
        <>
          <div className="hidden md:block">
            <div className="h-[55vh] overflow-y-auto md:border md:rounded-md">
              <GenericTable
                tableColumns={ATTENDANCE_COLUMNS || []}
                tableRows={attendance}
                columnLabels={ATTENDANCE_LABELS}
                className={""}
                customError={MESSAGES.NO_REQUESTS_AVAILABLE}
                renderField={renderFields}
              />
            </div>
            <Pagination
              page={pagination.page}
              totalPages={Math.max(
                1,
                Math.ceil(pagination.count / pagination.limit)
              )}
              rowsPerPage={pagination.limit}
              onPageChange={(newPage) =>
                setPagination((prev) => ({ ...prev, page: newPage }))
              }
              onPageSizeChange={(newLimit) =>
                setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }))
              }
            />
          </div>

          <div className="block md:hidden">
            <div className="h-[65vh] overflow-y-auto">
              {attendance.map((item) => (
                <MobileAttendanceCard
                  key={item.id}
                  data={item}
                  displayActionButtons={true}
                  handleApproval={handleStatusChange}
                />
              ))}
            </div>
            <Pagination
              page={pagination.page}
              totalPages={Math.ceil(pagination.count / pagination.limit)}
              rowsPerPage={pagination.limit}
              onPageChange={(newPage) =>
                setPagination((prev) => ({ ...prev, page: newPage }))
              }
              onPageSizeChange={(newLimit) =>
                setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }))
              }
            />
          </div>
        </>
      ) : (
        <div className="text-center">No data found</div>
      )}
    </div>
  );
};

export default ListAttendance;
